/**
 * Per-relationship emotional profiles.
 *
 * Each relationship (parent, spouse, child, friend, mentor…) deserves its own
 * cinematic register — pacing, typography, theme bias, and opening beats. Two
 * announcements with the same template but different relationships should
 * never feel like the same experience.
 *
 * Profiles are applied as biases on top of the adaptive style + theme picker:
 *   - `pacingScale`        — multiplier on the resolved pacing (>1 = slower)
 *   - `extraHeldBreathMs`  — additional pre-intro pause
 *   - `themeBias`          — emotional themes to weight up when scoring
 *   - `typographyOverride` — forces a typography preset for the message body
 *   - `introBeats`         — relationship-specific opening word sequences
 */

import type { EmotionalThemeId } from './emotionalThemes';
import type { RevealStylePreset } from './revealStyles';

export type RelationshipKey =
  | 'parent'
  | 'mother'
  | 'father'
  | 'grandparent'
  | 'sibling'
  | 'child'
  | 'spouse'
  | 'partner'
  | 'friend'
  | 'mentor'
  | 'colleague'
  | 'crush'
  | 'self'
  | 'other';

export interface RelationshipProfile {
  key: RelationshipKey;
  /** Multiplier on the resolved pacing. Higher = slower, more breath. */
  pacingScale: number;
  /** Extra ms of silence before the first intro word. */
  extraHeldBreathMs: number;
  /** Emotional themes to favor for this relationship. */
  themeBias: EmotionalThemeId[];
  /** Force a typography register for the body copy. */
  typographyOverride?: RevealStylePreset['typography'];
  /** Four-beat opening word sequences tailored to this relationship. */
  introBeats: Array<[string, string, string, string]>;
}

const PROFILES: Record<RelationshipKey, RelationshipProfile> = {
  parent: {
    key: 'parent',
    pacingScale: 1.18,
    extraHeldBreathMs: 600,
    themeBias: ['warm_nostalgia', 'golden_memory', 'through_the_years', 'eternal_bond'],
    typographyOverride: 'serif-classic',
    introBeats: [
      ['Before time pulls us further…', 'a child remembered.', 'A child wrote.', 'Read every word.'],
      ['You raised someone…', 'who never forgot.', 'These are their words.', 'Take your time.'],
    ],
  },
  mother: {
    key: 'mother',
    pacingScale: 1.22,
    extraHeldBreathMs: 700,
    themeBias: ['warm_nostalgia', 'eternal_bond', 'golden_memory', 'gentle_healing'],
    typographyOverride: 'serif-classic',
    introBeats: [
      ['Mom…', 'a few words.', 'Held for a lifetime.', 'Open them slowly.'],
      ['The first voice you knew…', 'is the one writing back.', 'Sit with this.', 'It begins now.'],
    ],
  },
  father: {
    key: 'father',
    pacingScale: 1.18,
    extraHeldBreathMs: 650,
    themeBias: ['warm_nostalgia', 'eternal_bond', 'legacy', 'through_the_years'],
    typographyOverride: 'serif-editorial',
    introBeats: [
      ['Dad…', 'this took years.', 'It found its day.', 'Read it slowly.'],
      ['Some words wait for a man…', 'to be ready to hear them.', 'You are.', 'Begin.'],
    ],
  },
  grandparent: {
    key: 'grandparent',
    pacingScale: 1.28,
    extraHeldBreathMs: 900,
    themeBias: ['through_the_years', 'legacy', 'warm_nostalgia', 'heavenlight'],
    typographyOverride: 'serif-editorial',
    introBeats: [
      ['Across generations…', 'a thank you found you.', 'Right on time.', 'Receive it.'],
      ['You are the root…', 'the rest of us grew from.', "Here's a reminder.", 'Open it.'],
    ],
  },
  sibling: {
    key: 'sibling',
    pacingScale: 1.08,
    extraHeldBreathMs: 350,
    themeBias: ['through_the_years', 'soul_connection', 'warm_nostalgia', 'light_after_darkness'],
    typographyOverride: 'mixed-magazine',
    introBeats: [
      ['Same blood. Same story.', 'Different chapters.', "Here's one of yours.", 'Open it.'],
      ['Before we were anything else…', 'we were each other.', 'Remember.', 'Read on.'],
    ],
  },
  child: {
    key: 'child',
    pacingScale: 1.20,
    extraHeldBreathMs: 700,
    themeBias: ['eternal_bond', 'golden_memory', 'gentle_healing', 'light_after_darkness'],
    typographyOverride: 'serif-handwritten',
    introBeats: [
      ['A parent tried…', 'to put it into words.', 'These are the ones.', 'Read them slowly.'],
      ['You may not understand yet…', 'but keep this.', "It's yours.", 'Always.'],
    ],
  },
  spouse: {
    key: 'spouse',
    pacingScale: 1.15,
    extraHeldBreathMs: 800,
    themeBias: ['soul_connection', 'eternal_bond', 'golden_memory', 'celebration_pulse'],
    typographyOverride: 'serif-editorial',
    introBeats: [
      ['Every day, a choice.', 'Today, again.', 'You.', 'Read every word.'],
      ['Of all the words you share…', 'these were saved for now.', 'Receive them.', 'Slowly.'],
    ],
  },
  partner: {
    key: 'partner',
    pacingScale: 1.12,
    extraHeldBreathMs: 750,
    themeBias: ['soul_connection', 'eternal_bond', 'golden_memory', 'celebration_pulse'],
    typographyOverride: 'serif-editorial',
    introBeats: [
      ['One heart…', 'speaking to another.', 'Nothing rehearsed.', 'Listen close.'],
      ['Some words…', 'only one person should hear.', 'You.', 'Open them.'],
    ],
  },
  friend: {
    key: 'friend',
    pacingScale: 1.05,
    extraHeldBreathMs: 300,
    themeBias: ['light_after_darkness', 'through_the_years', 'gentle_healing', 'warm_nostalgia'],
    typographyOverride: 'sans-modern',
    introBeats: [
      ['No special occasion.', 'Just a friend.', 'Just for you.', 'Open it.'],
      ['You may have forgotten…', 'how much you matter.', 'A friend remembered.', 'Read on.'],
    ],
  },
  mentor: {
    key: 'mentor',
    pacingScale: 1.18,
    extraHeldBreathMs: 700,
    themeBias: ['legacy', 'through_the_years', 'warm_nostalgia', 'inner_peace'],
    typographyOverride: 'serif-editorial',
    introBeats: [
      ['You shaped someone…', 'who carried it forward.', 'They came back to say so.', 'Read slowly.'],
      ['What you planted…', 'kept growing.', 'See for yourself.', 'Begin.'],
    ],
  },
  colleague: {
    key: 'colleague',
    pacingScale: 1.02,
    extraHeldBreathMs: 250,
    themeBias: ['warm_nostalgia', 'light_after_darkness', 'gentle_healing', 'celebration_pulse'],
    typographyOverride: 'sans-modern',
    introBeats: [
      ['Past the inbox…', 'past the meetings…', 'a colleague said something true.', 'Hear it.'],
      ['Work brought you together.', 'This is bigger than work.', 'Sit with it.', 'Read on.'],
    ],
  },
  crush: {
    key: 'crush',
    pacingScale: 1.10,
    extraHeldBreathMs: 600,
    themeBias: ['soul_connection', 'midnight_reflection', 'eternal_bond', 'light_after_darkness'],
    typographyOverride: 'serif-handwritten',
    introBeats: [
      ['Someone, somewhere…', 'has been thinking of you.', 'They finally wrote.', 'Read carefully.'],
      ['Unspoken for too long…', 'now spoken.', 'Just for you.', 'Begin.'],
    ],
  },
  self: {
    key: 'self',
    pacingScale: 1.20,
    extraHeldBreathMs: 800,
    themeBias: ['inner_peace', 'gentle_healing', 'midnight_reflection', 'light_after_darkness'],
    typographyOverride: 'sans-modern',
    introBeats: [
      ['You wrote this.', 'For who you would become.', "It's that person now.", 'Read gently.'],
      ['A letter to yourself…', 'across time.', 'Receive it.', 'Slowly.'],
    ],
  },
  other: {
    key: 'other',
    pacingScale: 1.08,
    extraHeldBreathMs: 400,
    themeBias: ['warm_nostalgia', 'gentle_healing', 'soul_connection'],
    introBeats: [
      ['Someone took the time…', 'to put this into words.', 'For you.', 'Read it slowly.'],
    ],
  },
};

/**
 * Resolve a relationship string to a profile. Handles synonyms and free-text
 * relationship labels by matching against known keywords.
 */
export function resolveRelationshipProfile(relationship?: string | null): RelationshipProfile {
  const raw = (relationship ?? '').toString().trim().toLowerCase();
  if (!raw) return PROFILES.other;

  // Exact key match first.
  if (raw in PROFILES) return PROFILES[raw as RelationshipKey];

  // Keyword routing for free-text relationship labels.
  if (/\b(mom|mum|mother|mama|mommy)\b/.test(raw)) return PROFILES.mother;
  if (/\b(dad|daddy|father|papa|pop)\b/.test(raw)) return PROFILES.father;
  if (/\b(grand|nana|nanna|nonna|grandma|grandpa|gran|abuela|abuelo|oma|opa)\b/.test(raw))
    return PROFILES.grandparent;
  if (/\b(brother|sister|sibling|bro|sis)\b/.test(raw)) return PROFILES.sibling;
  if (/\b(son|daughter|kid|child)\b/.test(raw)) return PROFILES.child;
  if (/\b(wife|husband|spouse|fianc)\b/.test(raw)) return PROFILES.spouse;
  if (/\b(partner|boyfriend|girlfriend|love|soulmate|beloved)\b/.test(raw)) return PROFILES.partner;
  if (/\b(friend|bestie|buddy|amigo|amiga)\b/.test(raw)) return PROFILES.friend;
  if (/\b(mentor|teacher|coach|professor)\b/.test(raw)) return PROFILES.mentor;
  if (/\b(colleague|coworker|teammate|boss|employee)\b/.test(raw)) return PROFILES.colleague;
  if (/\b(crush|admire|admirer|secret)\b/.test(raw)) return PROFILES.crush;
  if (/\b(self|me|myself|future me)\b/.test(raw)) return PROFILES.self;
  if (/\b(parent)\b/.test(raw)) return PROFILES.parent;

  return PROFILES.other;
}
